import { useEffect, useRef, useState, type ReactNode } from 'react';
import AparienciaMenu from './AparienciaMenu';
import { usePosicionDesplegable } from './ui-kit';

export type PanelNavItem = {
  key: string;
  label: string;
  icon: string;
  count?: number;
  children?: PanelNavItem[];
};

type PanelShellProps = {
  roleLabel: string;
  pageTitle: string;
  pageSubtitle: string;
  currentLabel: string;
  currentDetail: string;
  userName: string;
  userMeta: string;
  navItems: PanelNavItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  notifications?: ActivityItem[];
  onLogout: () => void;
  /** Muestra en celular/tablet el botón para alternar entre vista móvil y de escritorio. */
  viewModeToggle?: boolean;
  /** Muestra el botón de Apariencia (modo oscuro y estilo visual). */
  aparienciaPersonalizable?: boolean;
  children: ReactNode;
};

type ViewMode = 'movil' | 'escritorio';

const VIEW_MODE_STORAGE_KEY = 'freisacar.panel.viewMode';
const DESKTOP_VIEWPORT = 'width=1280';
const DEVICE_VIEWPORT = 'width=device-width, initial-scale=1.0';

function isTouchDevice(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

function readStoredViewMode(): ViewMode {
  try {
    return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'escritorio' ? 'escritorio' : 'movil';
  } catch {
    return 'movil';
  }
}

function applyViewport(content: string) {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (meta && meta.content !== content) meta.content = content;
}

/** Alterna el viewport del documento: en "escritorio" el navegador renderiza a 1280 px y escala. */
function useViewModeToggle(enabled: boolean) {
  const [available] = useState(() => enabled && isTouchDevice());
  const [mode, setMode] = useState<ViewMode>(() => (available ? readStoredViewMode() : 'movil'));

  useEffect(() => {
    if (!available) return;
    applyViewport(mode === 'escritorio' ? DESKTOP_VIEWPORT : DEVICE_VIEWPORT);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // Sin almacenamiento disponible: el modo dura solo esta sesión.
    }
  }, [available, mode]);

  // Al salir del panel (cerrar sesión) se restaura el viewport normal del dispositivo.
  useEffect(() => () => applyViewport(DEVICE_VIEWPORT), []);

  const toggle = () => {
    setMode(current => (current === 'escritorio' ? 'movil' : 'escritorio'));
    window.scrollTo(0, 0);
  };

  return { available, mode, toggle };
}

type StatCardProps = {
  icon: string;
  label: string;
  value: string;
  trend: string;
  trendTone?: 'up' | 'down' | 'neutral';
};

type ChartDatum = {
  label: string;
  value: number;
};

type ActivityItem = {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  meta: string;
  tone?: 'red' | 'black' | 'green' | 'yellow';
};

const toneClasses: Record<NonNullable<ActivityItem['tone']>, string> = {
  red: 'bg-[var(--freisa-red)]/12 text-[var(--freisa-red)]',
  black: 'bg-[var(--freisa-black)]/8 text-[var(--freisa-black)]',
  green: 'bg-emerald-100 text-emerald-700',
  yellow: 'bg-amber-100 text-amber-700',
};

const trendClasses: Record<NonNullable<StatCardProps['trendTone']>, string> = {
  up: 'bg-emerald-50 text-emerald-700',
  down: 'bg-red-50 text-red-700',
  neutral: 'bg-stone-100 text-stone-600',
};

export function PanelShell({
  roleLabel,
  pageTitle,
  pageSubtitle,
  currentLabel,
  currentDetail,
  userName,
  userMeta,
  navItems,
  activeKey,
  onSelect,
  notifications = [],
  onLogout,
  viewModeToggle = false,
  aparienciaPersonalizable = false,
  children,
}: PanelShellProps) {
  const viewMode = useViewModeToggle(viewModeToggle);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const navRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const primeraSeccionRef = useRef(true);
  const campanaRef = useRef<HTMLDivElement>(null);
  const posicionNotificaciones = usePosicionDesplegable(notificationsOpen, campanaRef);

  useEffect(() => {
    setSidebarOpen(false);
    setNotificationsOpen(false);
  }, [activeKey]);

  // Al cambiar de sección: la vista vuelve arriba, entra con un desvanecimiento corto
  // y el menú centra la opción elegida dentro de su propio desplazamiento.
  useEffect(() => {
    if (primeraSeccionRef.current) {
      primeraSeccionRef.current = false;
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      mainRef.current?.animate?.(
        [
          { opacity: 0, transform: 'translateY(10px)', filter: 'blur(3px)' },
          { opacity: 1, transform: 'none', filter: 'blur(0)' },
        ],
        { duration: 260, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
      );
    }
    centrarOpcionActiva();
  }, [activeKey]);

  useEffect(() => {
    if (sidebarOpen) centrarOpcionActiva(false);
  }, [sidebarOpen]);

  function centrarOpcionActiva(suave = true) {
    window.requestAnimationFrame(() => {
      const nav = navRef.current;
      const activo = nav?.querySelector<HTMLElement>('[data-nav-active="true"]');
      if (!nav || !activo) return;
      const destino = activo.offsetTop - nav.clientHeight / 2 + activo.offsetHeight / 2;
      nav.scrollTo({ top: Math.max(0, destino), behavior: suave ? 'smooth' : 'auto' });
    });
  }

  // El panel de notificaciones se cierra con Escape o con un clic fuera de él.
  useEffect(() => {
    if (!notificationsOpen) return undefined;
    const alClic = (event: MouseEvent) => {
      if (!notificationsRef.current?.contains(event.target as Node)) setNotificationsOpen(false);
    };
    const alTecla = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNotificationsOpen(false);
    };
    document.addEventListener('mousedown', alClic);
    window.addEventListener('keydown', alTecla);
    return () => {
      document.removeEventListener('mousedown', alClic);
      window.removeEventListener('keydown', alTecla);
    };
  }, [notificationsOpen]);

  return (
    <div className="min-h-screen bg-[var(--freisa-bg)] text-[var(--freisa-text)]">
      <div className="flex min-h-screen w-full">
        <button
          type="button"
          aria-label="Cerrar menú"
          tabIndex={sidebarOpen ? 0 : -1}
          onClick={() => setSidebarOpen(false)}
          className={`fixed inset-0 z-30 bg-black/45 backdrop-blur-[2px] transition-opacity duration-300 lg:hidden ${
            sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        />

        <aside
          className={`fixed inset-y-0 left-0 z-40 flex h-[100dvh] w-[300px] shrink-0 flex-col bg-[var(--freisa-black)] px-5 py-6 text-white shadow-[0_28px_80px_rgba(0,0,0,0.35)] transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:border-r lg:border-white/10 lg:shadow-none ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-14 shrink-0 items-center justify-center rounded-full bg-[var(--freisa-red)] text-base font-extrabold text-white shadow-[0_16px_32px_rgba(212,42,42,0.35)]">
                <span className="font-marca tracking-[0.16em]">FS</span>
              </div>
              <div>
                <p className="font-marca whitespace-nowrap text-base font-extrabold tracking-[0.16em] text-white">FREISACAR-GO</p>
                <p className="text-xs uppercase tracking-[0.22em] text-white/55">{roleLabel}</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Cerrar navegación"
              onClick={() => setSidebarOpen(false)}
              className="shrink-0 rounded-xl border border-white/10 px-3 py-2 text-sm text-white/70 transition hover:border-white/30 hover:text-white lg:hidden"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-6 flex items-center gap-3 rounded-[var(--radius-panel)] border border-white/10 bg-white/5 px-4 py-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm font-bold uppercase text-white">
              {(userName || '?').trim().charAt(0)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white" title={userName}>{userName}</p>
              <p className="truncate text-xs uppercase tracking-[0.16em] text-white/55">{userMeta}</p>
            </div>
          </div>

          <nav ref={navRef} className="scroll-oscuro relative mt-5 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain pr-1">
            {navItems.map(item => {
              const hasChildren = Boolean(item.children?.length);
              const childSelected = item.children?.some(child => child.key === activeKey) ?? false;
              const selected = item.key === activeKey || childSelected;
              const expanded = openGroups[item.key] ?? childSelected;

              if (hasChildren) {
                return (
                  <div key={item.key} className="shrink-0 space-y-2">
                    <button
                      type="button"
                      onClick={() => setOpenGroups(prev => ({ ...prev, [item.key]: !expanded }))}
                      aria-expanded={expanded}
                      data-nav-active={selected && !expanded ? 'true' : undefined}
                      className={`flex w-full items-center justify-between rounded-[var(--radius-panel)] border px-4 py-3 text-left transition duration-200 ${
                        selected
                          ? 'border-[var(--freisa-red)] bg-[var(--freisa-red)] text-white shadow-[0_18px_34px_rgba(212,42,42,0.24)]'
                          : 'border-white/10 bg-white/4 text-white/72 hover:border-white/18 hover:bg-white/8 hover:text-white'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${selected ? 'bg-white/12 text-white' : 'bg-white/6 text-white/70'}`}>
                          <i className={item.icon} aria-hidden="true" />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold">{item.label}</span>
                        </span>
                      </span>
                      <span className="flex items-center gap-2">
                        {item.count !== undefined && (
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${selected ? 'bg-white/14 text-white' : 'bg-white/8 text-white/70'}`}>
                            {item.count}
                          </span>
                        )}
                        <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                      </span>
                    </button>

                    <div
                      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                      aria-hidden={!expanded}
                    >
                      <div className="ml-5 min-h-0 space-y-2 overflow-hidden border-l border-white/10 pl-3">
                        {(item.children ?? []).map(child => {
                          const childSelectedExact = child.key === activeKey;

                          return (
                            <button
                              key={child.key}
                              type="button"
                              tabIndex={expanded ? 0 : -1}
                              onClick={() => { setSidebarOpen(false); onSelect(child.key); }}
                              data-nav-active={childSelectedExact ? 'true' : undefined}
                              aria-current={childSelectedExact ? 'page' : undefined}
                              className={`flex w-full items-center justify-between rounded-[16px] border px-3 py-2.5 text-left transition duration-200 ${
                                childSelectedExact
                                  ? 'border-white/20 bg-white/14 text-white'
                                  : 'border-white/8 bg-white/3 text-white/65 hover:translate-x-0.5 hover:border-white/16 hover:bg-white/8 hover:text-white'
                              }`}
                            >
                              <span className="flex items-center gap-3">
                                <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${childSelectedExact ? 'bg-white/12 text-white' : 'bg-white/6 text-white/60'}`}>
                                  <i className={child.icon} aria-hidden="true" />
                                </span>
                                <span className="text-sm font-semibold">{child.label}</span>
                              </span>
                              {child.count !== undefined && (
                                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${childSelectedExact ? 'bg-white/14 text-white' : 'bg-white/8 text-white/70'}`}>
                                  {child.count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => { setSidebarOpen(false); onSelect(item.key); }}
                  data-nav-active={selected ? 'true' : undefined}
                  aria-current={selected ? 'page' : undefined}
                  className={`flex shrink-0 items-center justify-between rounded-[var(--radius-panel)] border px-4 py-3 text-left transition duration-200 ${
                    selected
                      ? 'border-[var(--freisa-red)] bg-[var(--freisa-red)] text-white shadow-[0_18px_34px_rgba(212,42,42,0.24)]'
                      : 'border-white/10 bg-white/4 text-white/72 hover:translate-x-0.5 hover:border-white/18 hover:bg-white/8 hover:text-white'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${selected ? 'bg-white/12 text-white' : 'bg-white/6 text-white/70'}`}>
                      <i className={item.icon} aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold">{item.label}</span>
                    </span>
                  </span>
                  {item.count !== undefined && (
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${selected ? 'bg-white/14 text-white' : 'bg-white/8 text-white/70'}`}>
                      {item.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

        </aside>

        <div className="flex min-h-screen min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 border-b border-[var(--freisa-border)] bg-white/95 px-3 py-2.5 backdrop-blur sm:px-4 lg:px-8 lg:py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <button
                  type="button"
                  aria-label="Abrir navegación"
                  onClick={() => setSidebarOpen(true)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-control)] border border-[var(--freisa-border)] text-[var(--freisa-black)] transition hover:border-[var(--freisa-red)] hover:text-[var(--freisa-red)] lg:hidden"
                >
                  <i className="fa-solid fa-bars text-sm" aria-hidden="true" />
                </button>
                <div className="min-w-0">
                  <p className="hidden text-[10px] font-semibold uppercase tracking-[0.26em] text-[var(--freisa-text-muted)] sm:block">{pageTitle}</p>
                  <h1 className="truncate text-lg font-semibold tracking-tight text-[var(--freisa-text)] lg:text-xl">{currentLabel}</h1>
                  <p className="hidden max-w-3xl truncate text-xs text-[var(--freisa-text-soft)] md:block" title={currentDetail || pageSubtitle}>
                    {currentDetail || pageSubtitle}
                  </p>
                </div>
              </div>

              <div ref={notificationsRef} className="relative flex shrink-0 items-center justify-end gap-1.5 sm:gap-2">
                {aparienciaPersonalizable && <AparienciaMenu />}
                <div ref={campanaRef}>
                  <TopActionButton
                    icon="fa-regular fa-bell"
                    label="Notificaciones"
                    notify={notifications.length > 0}
                    badgeCount={notifications.length}
                    onClick={() => setNotificationsOpen(current => !current)}
                  />
                </div>
                {notificationsOpen && (
                  <div style={posicionNotificaciones} className="anim-pop-in absolute right-0 top-[calc(100%+12px)] z-30 max-h-[70vh] w-[min(360px,calc(100vw-2rem))] overflow-y-auto rounded-[var(--radius-panel)] border border-[var(--freisa-border)] bg-white p-4 shadow-[0_24px_60px_rgba(26,26,26,0.16)]">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-[var(--freisa-text)]">Notificaciones</p>
                        <p className="mt-1 text-xs text-[var(--freisa-text-soft)]">Pendientes que requieren atención.</p>
                      </div>
                      <span className="rounded-full bg-[var(--freisa-black)] px-2.5 py-1 text-xs font-semibold text-white">
                        {notifications.length}
                      </span>
                    </div>

                    <div className="mt-4 space-y-3">
                      {notifications.length === 0 ? (
                        <div className="rounded-[var(--radius-panel)] border border-dashed border-[var(--freisa-border)] bg-[var(--freisa-bg)] px-4 py-5 text-center text-sm text-[var(--freisa-text-muted)]">
                          Sin notificaciones pendientes.
                        </div>
                      ) : (
                        notifications.map(item => (
                          <article key={item.id} className="flex items-start gap-3 rounded-[var(--radius-panel)] border border-[var(--freisa-border)] bg-[var(--freisa-bg)]/70 p-3">
                            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${toneClasses[item.tone ?? 'red']}`}>
                              <i className={item.icon} aria-hidden="true" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-[var(--freisa-text)]">{item.title}</p>
                              <p className="mt-1 text-sm text-[var(--freisa-text-soft)]">{item.subtitle}</p>
                            </div>
                            {item.meta ? (
                              <span className="text-[11px] font-medium text-[var(--freisa-text-muted)]">{item.meta}</span>
                            ) : null}
                          </article>
                        ))
                      )}
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={onLogout}
                  title="Cerrar sesión"
                  aria-label="Cerrar sesión"
                  className="ui-btn ui-btn-dark h-9 !px-3 !text-xs hover:!bg-[var(--freisa-red)]"
                >
                  <i className="fa-solid fa-right-from-bracket" aria-hidden="true" />
                  <span className="hidden lg:inline">Salir</span>
                </button>
                {viewMode.available && (
                  <button
                    type="button"
                    onClick={viewMode.toggle}
                    title={viewMode.mode === 'escritorio' ? 'Cambiar a vista móvil' : 'Cambiar a vista de escritorio'}
                    aria-label={viewMode.mode === 'escritorio' ? 'Cambiar a vista móvil' : 'Cambiar a vista de escritorio'}
                    className="ui-btn ui-btn-secondary ui-btn-sm flex h-9 min-w-9 items-center justify-center gap-2 !px-2.5 !text-xs"
                  >
                    <i
                      className={viewMode.mode === 'escritorio' ? 'fa-solid fa-mobile-screen-button' : 'fa-solid fa-desktop'}
                      aria-hidden="true"
                    />
                    <span className="hidden lg:inline">
                      {viewMode.mode === 'escritorio' ? 'Vista móvil' : 'Vista escritorio'}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </header>

          <main ref={mainRef} className="min-w-0 flex-1 px-4 py-5 lg:px-8 lg:py-7">{children}</main>
        </div>
      </div>
    </div>
  );
}

function TopActionButton({
  icon,
  label,
  notify = false,
  badgeCount,
  onClick,
}: {
  icon: string;
  label: string;
  notify?: boolean;
  badgeCount?: number;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="relative flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] border border-[var(--freisa-border)] bg-white text-sm text-[var(--freisa-black)] transition hover:-translate-y-0.5 hover:border-[var(--freisa-red)] hover:text-[var(--freisa-red)]"
    >
      <i className={icon} aria-hidden="true" />
      {notify && badgeCount ? (
        <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--freisa-red)] px-1 text-[9px] font-bold text-white">
          {badgeCount}
        </span>
      ) : null}
    </button>
  );
}

export function SectionSurface({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-[var(--radius-card)] border border-[var(--freisa-border)] bg-white p-5 shadow-[0_18px_40px_rgba(26,26,26,0.06)] ${className}`}>
      {children}
    </section>
  );
}

export function DashboardStatCard({ icon, label, value, trend, trendTone = 'neutral' }: StatCardProps) {
  return (
    <article className="group rounded-[var(--radius-panel)] border border-[var(--freisa-border)] bg-white p-5 shadow-[0_16px_34px_rgba(26,26,26,0.05)] transition duration-200 hover:-translate-y-1.5 hover:shadow-[0_22px_46px_rgba(26,26,26,0.12)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-[var(--freisa-text-soft)]">{label}</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-[var(--freisa-text)]">{value}</p>
        </div>
        <span className="flex h-12 w-12 items-center justify-center rounded-[16px] bg-[var(--freisa-red)]/10 text-[var(--freisa-red)] transition group-hover:bg-[var(--freisa-red)] group-hover:text-white">
          <i className={`${icon} text-lg`} aria-hidden="true" />
        </span>
      </div>
      <span className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${trendClasses[trendTone]}`}>
        <i className={`fa-solid ${trendTone === 'down' ? 'fa-arrow-trend-down' : trendTone === 'up' ? 'fa-arrow-trend-up' : 'fa-minus'}`} aria-hidden="true" />
        {trend}
      </span>
    </article>
  );
}

export function WeeklyBarChart({ title, subtitle, data }: { title: string; subtitle: string; data: ChartDatum[] }) {
  const maxValue = Math.max(...data.map(item => item.value), 1);
  const total = data.reduce((suma, item) => suma + item.value, 0);
  // La serie del backend va de lunes a domingo de la semana actual.
  const hoy = (new Date().getDay() + 6) % 7;
  const [montado, setMontado] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setMontado(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <SectionSurface>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-[var(--freisa-text)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--freisa-text-soft)]">{subtitle}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-semibold tracking-tight text-[var(--freisa-text)]">{total}</p>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--freisa-text-muted)]">esta semana</p>
        </div>
      </div>

      <div className="mt-6 grid h-64 grid-cols-7 items-end gap-2 sm:gap-3">
        {data.map((item, index) => {
          const height = Math.max((item.value / maxValue) * 100, item.value > 0 ? 8 : 3);
          const esHoy = index === hoy;
          const futuro = index > hoy;

          return (
            <div
              key={item.label}
              className="group flex h-full flex-col items-center justify-end gap-2"
              title={`${item.label}: ${item.value} ${item.value === 1 ? 'ruta' : 'rutas'}${esHoy ? ' (hoy)' : ''}`}
            >
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold transition ${
                  esHoy ? 'bg-[var(--freisa-red)] text-white' : 'text-[var(--freisa-text-soft)] group-hover:bg-stone-100'
                }`}
              >
                {futuro && item.value === 0 ? '' : item.value}
              </span>
              <div className="flex h-full w-full items-end">
                <div
                  className={`w-full rounded-t-[10px] transition-[height,background-color,opacity] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    esHoy
                      ? 'bg-[var(--freisa-red)]'
                      : futuro
                        ? 'bg-stone-200'
                        : 'bg-stone-800/85 group-hover:bg-stone-900'
                  }`}
                  style={{ height: montado ? `${height}%` : '0%', transitionDelay: `${index * 40}ms` }}
                />
              </div>
              <span className={`text-xs font-semibold uppercase tracking-[0.14em] ${esHoy ? 'text-[var(--freisa-red)]' : 'text-[var(--freisa-text-muted)]'}`}>
                {item.label}
              </span>
            </div>
          );
        })}
      </div>
    </SectionSurface>
  );
}

export function ActivityFeed({ title, subtitle, items }: { title: string; subtitle: string; items: ActivityItem[] }) {
  return (
    <SectionSurface>
      <h3 className="text-lg font-semibold text-[var(--freisa-text)]">{title}</h3>
      <p className="mt-1 text-sm text-[var(--freisa-text-soft)]">{subtitle}</p>

      <div className="mt-5 space-y-3">
        {items.length === 0 ? (
          <div className="rounded-[var(--radius-panel)] border border-dashed border-[var(--freisa-border)] bg-[var(--freisa-bg)] px-4 py-6 text-center text-sm text-[var(--freisa-text-muted)]">
            No hay actividad reciente para mostrar.
          </div>
        ) : (
          items.map(item => (
            <article key={item.id} className="flex items-start gap-3 rounded-[var(--radius-panel)] border border-[var(--freisa-border)] bg-[var(--freisa-bg)]/70 p-3">
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${toneClasses[item.tone ?? 'red']}`}>
                <i className={item.icon} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[var(--freisa-text)]">{item.title}</p>
                <p className="mt-1 text-sm text-[var(--freisa-text-soft)]">{item.subtitle}</p>
              </div>
              <span className="text-xs font-medium text-[var(--freisa-text-muted)]">{item.meta}</span>
            </article>
          ))
        )}
      </div>
    </SectionSurface>
  );
}